/**
 * Admin review queue for the Dunea marketplace: in_review versions oldest
 * first, the submitted manifest with the risky parts highlighted, automated
 * checks, approve / reject (notes required), and listing / developer actions.
 * Admin only (the API enforces it too).
 */
import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { ExpertSelfView, VersionView } from '@dunea/shared';
import {
  apiAdminListingCharges,
  apiAdminRefund,
  apiApproveVersion,
  apiFeatureListing,
  apiGetReviewQueue,
  apiListPendingExperts,
  apiRejectVersion,
  apiReviewExpert,
  apiSuspendListing,
  apiUnsuspendListing,
  apiVerifyDeveloper,
  type AdminListingCharge,
} from '~/lib/api/marketplace';
import { formatMinor, majorToMinor } from '~/components/workbench/tools/payments/payments-model';
import { $currentUser } from '~/lib/stores/auth';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { permissionLabel } from '~/components/workbench/tools/marketplace/marketplace-model';
import { PageHeading, PageShell } from '~/components/marketplace/PageShell';
import {
  failingChecks,
  manifestHighlights,
  manifestJsonLines,
  rejectionError,
  rejectionNotes,
  RULE_GROUPS,
  sortQueue,
  toggleCode,
  waitingFor,
} from '~/components/marketplace/admin-model';
import { countryName, serviceLine, portfolioLinks } from '~/components/workbench/tools/marketplace/community-model';
import { apiErrorMessage } from '~/components/marketplace/developer-model';

export default function MarketplaceReviewPage() {
  const user = useStore($currentUser);

  if (user?.role !== 'admin') {
    return (
      <PageShell crumb="Review queue">
        <div className={classNames(CARD, 'px-4 py-8 text-center text-[12.5px] text-muted')}>
          The review queue is for Dunea admins.{' '}
          <Link to="/developers" className="text-secondary underline underline-offset-2">
            Go to the developer portal
          </Link>
        </div>
      </PageShell>
    );
  }

  return <ReviewQueue />;
}

const ADMIN_TABS = [
  ['versions', 'Versions'],
  ['experts', 'Experts'],
  ['refunds', 'Refunds'],
] as const;

function ReviewQueue() {
  const [tab, setTab] = useState<'versions' | 'experts' | 'refunds'>('versions');
  const queue = useResource(() => apiGetReviewQueue(), 'admin-queue');
  const [selected, setSelected] = useState<string | null>(null);
  const items = queue.data ? sortQueue(queue.data) : [];
  const current = items.find((v) => v.id === selected) ?? items[0] ?? null;

  const resolved = (id: string) => {
    queue.setData((prev) => (prev ?? []).filter((v) => v.id !== id));
    setSelected(null);
  };

  return (
    <PageShell
      crumb="Review queue"
      aside={
        <Link to="/developers" className={BTN_GHOST}>
          Developer portal
        </Link>
      }
    >
      <PageHeading
        caption="Marketplace review"
        title={
          queue.data ? (
            items.length ? (
              <>
                {items.length} waiting, <i className="text-accent">oldest first.</i>
              </>
            ) : (
              <>
                Queue <i className="text-accent">clear.</i>
              </>
            )
          ) : (
            'Review queue'
          )
        }
      />

      <div className="mb-6">
        <Segmented value={tab} options={ADMIN_TABS} onChange={setTab} label="Review sections" />
      </div>

      {tab === 'experts' ? (
        <ExpertsQueue />
      ) : tab === 'refunds' ? (
        <RefundsConsole />
      ) : queue.error ? (
        <LoadError message="Could not load the review queue." onRetry={() => void queue.reload()} />
      ) : !queue.data ? (
        <SkeletonCard lines={4} />
      ) : items.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-8 text-center text-[12.5px] text-muted')}>Nothing in review.</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <ul className="space-y-1.5">
            {items.map((v) => {
              const failing = failingChecks(v.checks);

              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(v.id)}
                    className={classNames(
                      CARD,
                      'w-full px-3.5 py-2.5 text-left transition-colors',
                      current?.id === v.id ? '!border-prominent' : 'hover:border-subtle',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-geist-mono text-[12px] text-primary">{v.slug}</span>
                      <span className="shrink-0 text-[11px] text-muted">{waitingFor(v.submittedAt)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted">
                      <span>v{v.version}</span>
                      {v.developer && <span>@{v.developer.handle}</span>}
                      {v.reviewMode === 'auto' && <span className="text-success">Auto-approved</span>}
                      {failing > 0 && <span className="text-danger">{failing} failing</span>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {current && <VersionReview key={current.id} version={current} onResolved={() => resolved(current.id)} />}
        </div>
      )}
    </PageShell>
  );
}

function VersionReview({ version, onResolved }: { version: VersionView; onResolved: () => void }) {
  const highlights = manifestHighlights(version.manifest, version.checks);
  const lines = manifestJsonLines(version.manifest);

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-geist-mono text-[15px] text-primary">
          {version.slug} <span className="text-muted">v{version.version}</span>
        </h2>
        {version.developer && (
          <span className="inline-flex items-center gap-1 text-[12px] text-secondary">
            @{version.developer.handle}
            {version.developer.verified && <span className="i-ph:seal-check-fill text-[12px] text-info" />}
          </span>
        )}
        <span className="text-[11px] text-muted">submitted {formatDate(version.submittedAt)}</span>
        {version.reviewMode === 'auto' && <Pill tone="success" label="Auto-approved" />}
      </div>
      {version.reviewMode === 'auto' && (
        <p className="text-[12px] text-secondary">
          This update was fast-tracked and is already live. Reject it to pull it and tell the developer why.
        </p>
      )}

      {version.changelog && (
        <p className="whitespace-pre-line text-[12.5px] leading-relaxed text-secondary">{version.changelog}</p>
      )}

      <Section title="Automated checks" icon="i-ph:list-checks">
        {version.checks.length === 0 ? (
          <p className="text-[12px] text-muted">No checks reported.</p>
        ) : (
          <ul className={classNames(CARD, 'divide-y divide-hairline')}>
            {version.checks.map((c) => (
              <li key={c.name} className="flex items-start gap-2.5 px-4 py-2 text-[12px]">
                <span
                  className={classNames(
                    'mt-0.5 shrink-0 text-[13px]',
                    c.ok ? 'i-ph:check-circle text-success' : 'i-ph:x-circle text-danger',
                  )}
                />
                <span className="min-w-0 text-secondary">
                  <span className="font-geist-mono text-[11px] text-primary">{c.name}</span> {c.message}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Look closely" icon="i-ph:eye">
        <div className={classNames(CARD, 'space-y-4 p-4')}>
          <div className="space-y-1.5">
            <h4 className={CAPTION}>Permissions</h4>
            {highlights.permissions.length === 0 ? (
              <p className="text-[12px] text-muted">None requested.</p>
            ) : (
              <ul className="space-y-1">
                {highlights.permissions.map((p) => {
                  const isNew = highlights.newPermissions.includes(p);

                  return (
                    <li key={p} className="flex flex-wrap items-center gap-2 text-[12px]">
                      <code
                        className={classNames('font-geist-mono text-[11px]', isNew ? 'text-warning' : 'text-primary')}
                      >
                        {p}
                      </code>
                      <span className="text-muted">{permissionLabel(p)}</span>
                      {isNew && <Pill tone="warning" label="New" />}
                    </li>
                  );
                })}
              </ul>
            )}
            {highlights.newPermissionsNote && (
              <p className="text-[11.5px] text-warning">{highlights.newPermissionsNote}</p>
            )}
          </div>

          <UrlRow label="Webhook URL" url={highlights.webhookUrl} />
          <UrlRow label="Panel URL" url={highlights.panelUrl} />

          <div className="space-y-1.5">
            <h4 className={CAPTION}>Site scripts</h4>
            {highlights.siteScripts.length === 0 ? (
              <p className="text-[12px] text-muted">None.</p>
            ) : (
              <ul className="space-y-1">
                {highlights.siteScripts.map((s) => (
                  <li key={s.src} className="flex flex-wrap items-center gap-2 text-[12px]">
                    <code className="break-all font-geist-mono text-[11px] text-warning">{s.src}</code>
                    <span className="text-muted">
                      {s.consent} · {s.placement}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {highlights.agentTools.length > 0 && (
            <div className="space-y-1.5">
              <h4 className={CAPTION}>AI tools</h4>
              <ul className="space-y-1">
                {highlights.agentTools.map((t) => (
                  <li key={t.name} className="text-[12px] text-secondary">
                    <code className="font-geist-mono text-[11px] text-primary">{t.name}</code> {t.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>

      <Section title="Manifest" icon="i-ph:brackets-curly">
        <pre className="max-h-[480px] overflow-auto rounded-xl border border-hairline bg-sunken py-3 font-geist-mono text-[11px] leading-[1.6]">
          {lines.map((l, i) => (
            <div
              key={i}
              className={classNames(
                'whitespace-pre px-4',
                l.highlight
                  ? 'border-l-2 border-warning/60 bg-warning/[0.06] text-primary'
                  : 'border-l-2 border-transparent text-secondary',
              )}
            >
              {l.text}
            </div>
          ))}
        </pre>
      </Section>

      <Decision version={version} onResolved={onResolved} />
      <ListingActions version={version} />
    </div>
  );
}

function UrlRow({ label, url }: { label: string; url: string | null }) {
  return (
    <div className="space-y-1">
      <h4 className={CAPTION}>{label}</h4>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all font-geist-mono text-[11.5px] text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
        >
          {url}
        </a>
      ) : (
        <p className="text-[12px] text-muted">None.</p>
      )}
    </div>
  );
}

/** Approve (notes optional) or reject (cite at least one checklist rule) — the page's single lime action. */
function Decision({ version, onResolved }: { version: VersionView; onResolved: () => void }) {
  const [notes, setNotes] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (kind: 'approve' | 'reject') => {
    if (kind === 'reject') {
      const problem = rejectionError(codes);

      if (problem) {
        setError(problem);
        return;
      }
    }

    setBusy(kind);
    setError(null);

    try {
      if (kind === 'approve') {
        await apiApproveVersion(version.id, notes);
      } else {
        await apiRejectVersion(version.id, rejectionNotes(codes, notes), codes);
      }

      toast.success(`${version.slug} v${version.version} ${kind === 'approve' ? 'approved' : 'rejected'}.`);
      onResolved();
    } catch (err) {
      setError(apiErrorMessage(err, `Could not ${kind} this version.`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className={classNames(CARD, 'space-y-4 p-4')}>
      <div className="space-y-2">
        <h4 className={CAPTION}>Rules it breaks (required to reject)</h4>
        <div className="grid gap-3 sm:grid-cols-2">
          {RULE_GROUPS.map((g) => (
            <fieldset key={g.label} className="space-y-1">
              <legend className="mb-1 text-[11px] text-secondary">{g.label}</legend>
              {g.rules.map((r) => (
                <label key={r.id} className="flex cursor-pointer items-start gap-2 text-[11.5px] leading-relaxed">
                  <input
                    type="checkbox"
                    checked={codes.includes(r.id)}
                    onChange={() => setCodes((c) => toggleCode(c, r.id))}
                    className="mt-0.5 shrink-0 accent-accent"
                  />
                  <span className="text-muted">
                    <code className="mr-1 font-geist-mono text-[10.5px] text-primary">{r.id}</code>
                    {r.text}
                  </span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
      </div>
      <Field label="Review notes (shown to the developer)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="ui-input resize-y" />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => void act('approve')} disabled={busy !== null} className="ui-btn-accent">
          {busy === 'approve' && <Spinner />}
          Approve
        </button>
        <button
          type="button"
          onClick={() => void act('reject')}
          disabled={busy !== null}
          className={classNames(BTN_SECONDARY, '!border-danger/40 !text-danger')}
        >
          {busy === 'reject' && <Spinner />}
          Reject{codes.length > 0 && ` (${codes.length})`}
        </button>
      </div>
    </section>
  );
}

// ─── experts ───

function ExpertsQueue() {
  const pending = useResource(() => apiListPendingExperts(), 'admin-experts');

  if (pending.error) {
    return <LoadError message="Could not load pending experts." onRetry={() => void pending.reload()} />;
  }

  if (!pending.data) {
    return <SkeletonCard lines={3} />;
  }

  if (pending.data.length === 0) {
    return (
      <div className={classNames(CARD, 'px-4 py-8 text-center text-[12.5px] text-muted')}>No experts waiting.</div>
    );
  }

  return (
    <ul className="space-y-3">
      {pending.data.map((e) => (
        <ExpertReview
          key={e.id}
          expert={e}
          onResolved={() => pending.setData((prev) => (prev ?? []).filter((x) => x.id !== e.id))}
        />
      ))}
    </ul>
  );
}

function ExpertReview({ expert, onResolved }: { expert: ExpertSelfView; onResolved: () => void }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const links = portfolioLinks(expert.portfolio);

  const act = async (decision: 'approve' | 'reject') => {
    setBusy(decision);

    try {
      await apiReviewExpert(expert.id, decision, notes);
      toast.success(`@${expert.handle} ${decision === 'approve' ? 'approved' : 'rejected'}.`);
      onResolved();
    } catch (err) {
      toast.error(apiErrorMessage(err, `Could not ${decision} this expert.`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className={classNames(CARD, 'space-y-3 p-4')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-primary">{expert.displayName}</span>
        <span className="text-[11.5px] text-muted">@{expert.handle}</span>
        {expert.verified && <Pill tone="info" label="Verified developer" />}
      </div>
      <p className="text-[12.5px] text-secondary">{expert.headline}</p>
      <p className="whitespace-pre-line text-[12px] leading-relaxed text-muted">{expert.about}</p>
      <div className="flex flex-wrap gap-x-3 text-[11px] text-muted">
        <span>{expert.countries.map(countryName).join(', ') || 'No countries'}</span>
        <span>{expert.languages.join(', ')}</span>
      </div>
      {expert.services.length > 0 && (
        <ul className="divide-y divide-hairline rounded-lg border border-hairline">
          {expert.services.map((s) => (
            <li key={s.title} className="flex justify-between gap-3 px-3 py-1.5 text-[12px]">
              <span className="text-primary">{s.title}</span>
              <span className="shrink-0 text-muted">{serviceLine(s)}</span>
            </li>
          ))}
        </ul>
      )}
      {links.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11.5px] text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes for the expert (optional)"
          aria-label="Notes"
          className="ui-input !py-2"
        />
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={() => void act('approve')} disabled={busy !== null} className={BTN_SECONDARY}>
            {busy === 'approve' && <Spinner />}
            Approve
          </button>
          <button
            type="button"
            onClick={() => void act('reject')}
            disabled={busy !== null}
            className={classNames(BTN_SECONDARY, '!border-danger/40 !text-danger')}
          >
            {busy === 'reject' && <Spinner />}
            Reject
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Listing + developer moderation. VersionView doesn't carry the listing's
 * featured/suspended state or the developer's verified flag history, so both
 * directions are offered.
 */
function ListingActions({ version }: { version: VersionView }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (key: string, fn: () => Promise<void>, done: string): Promise<boolean> => {
    setBusy(key);

    try {
      await fn();
      toast.success(done);

      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That action failed.'));

      return false;
    } finally {
      setBusy(null);
    }
  };

  const handle = version.developer?.handle;

  return (
    <Section title="Listing & developer" icon="i-ph:shield-check">
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            busy={busy === 'feature'}
            onClick={() => void run('feature', () => apiFeatureListing(version.slug, true), 'Listing featured.')}
            label="Feature"
          />
          <ActionButton
            busy={busy === 'unfeature'}
            onClick={() => void run('unfeature', () => apiFeatureListing(version.slug, false), 'Listing unfeatured.')}
            label="Unfeature"
          />
          <ActionButton
            busy={busy === 'unsuspend'}
            onClick={() => void run('unsuspend', () => apiUnsuspendListing(version.slug), 'Listing unsuspended.')}
            label="Unsuspend"
          />
          {handle && (
            <>
              <ActionButton
                busy={busy === 'verify'}
                onClick={() => void run('verify', () => apiVerifyDeveloper(handle, true), `@${handle} verified.`)}
                label={`Verify @${handle}`}
              />
              <ActionButton
                busy={busy === 'unverify'}
                onClick={() => void run('unverify', () => apiVerifyDeveloper(handle, false), `@${handle} unverified.`)}
                label="Remove verification"
              />
            </>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Suspension reason (shown to the developer)"
            aria-label="Suspension reason"
            className="ui-input !py-2"
          />
          <button
            type="button"
            disabled={busy !== null || reason.trim().length < 5}
            onClick={() =>
              void run('suspend', () => apiSuspendListing(version.slug, reason), 'Listing suspended.').then(
                (ok) => ok && setReason(''),
              )
            }
            className={classNames(BTN_SECONDARY, 'shrink-0 !border-danger/40 !text-danger')}
          >
            {busy === 'suspend' && <Spinner />}
            Suspend listing
          </button>
        </div>
      </div>
    </Section>
  );
}

/**
 * Admin refunds of marketplace purchases. Refunds go through Stripe with the
 * developer's transfer and Dunea's fee reversed pro rata; the earnings ledger
 * updates at once. Admin-only until a marketplace refund policy is decided.
 */
function RefundsConsole() {
  const [slug, setSlug] = useState('');
  const [loaded, setLoaded] = useState('');
  const charges = useResource(loaded ? () => apiAdminListingCharges(loaded) : null, `admin-refunds:${loaded}`);

  return (
    <Section title="Refund a purchase" icon="i-ph:arrow-counter-clockwise">
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (slug.trim() === loaded) {
            void charges.reload();
          }

          setLoaded(slug.trim());
        }}
        className="flex flex-col gap-2 sm:flex-row"
      >
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="Listing slug, e.g. acme-reviews"
          aria-label="Listing slug"
          spellCheck={false}
          className="ui-input-mono !py-2"
        />
        <button type="submit" disabled={!slug.trim()} className={classNames(BTN_SECONDARY, 'shrink-0')}>
          Load charges
        </button>
      </form>

      {!loaded ? (
        <p className={CAPTION}>
          Sales and subscription invoices of a listing. The buyer gets the money back; the developer&apos;s share and
          Dunea&apos;s fee are reversed in proportion. Usage charges are refunded in credits instead.
        </p>
      ) : charges.error ? (
        <LoadError message="Could not load this listing's charges." onRetry={() => void charges.reload()} />
      ) : !charges.data ? (
        <SkeletonCard lines={3} />
      ) : charges.data.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center text-[12.5px] text-muted')}>
          No paid charges for {loaded}.
        </div>
      ) : (
        <ul className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {charges.data.map((c) => (
            <RefundRow key={c.stripeRef} charge={c} onRefunded={() => void charges.reload()} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function RefundRow({ charge, onRefunded }: { charge: AdminListingCharge; onRefunded: () => void }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remaining = charge.gross - charge.refundedGross;

  const submit = async () => {
    setError(null);

    const cents = amount.trim() ? majorToMinor(amount, charge.currency) : remaining;

    if (cents === null || cents < 1 || cents > remaining) {
      setError(`Enter an amount up to ${formatMinor(remaining, charge.currency)}, or leave it blank for all of it.`);
      return;
    }

    if (!confirming) {
      setConfirming(true);
      return;
    }

    setBusy(true);

    try {
      const result = await apiAdminRefund({
        stripeRef: charge.stripeRef,
        ...(cents !== remaining ? { amount: cents } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(
        `Refunded ${formatMinor(result.amount, charge.currency)}${result.full ? ' (in full; the install is suspended for one-time purchases)' : ''}.`,
      );
      setAmount('');
      setNote('');
      setConfirming(false);
      onRefunded();
    } catch (err) {
      setError(apiErrorMessage(err, 'The refund failed.'));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-geist-mono text-[12px] text-primary">{charge.stripeRef}</div>
          <div className="text-[11px] text-muted">
            {charge.stream === 'subscription' ? 'Subscription invoice' : 'One-time sale'} ·{' '}
            {formatDate(charge.occurredAt)} · {formatMinor(charge.gross, charge.currency)}
            {charge.refundedGross > 0 && ` · ${formatMinor(charge.refundedGross, charge.currency)} refunded`}
          </div>
        </div>
        {remaining <= 0 && <Pill tone="muted" label="Refunded" />}
      </div>

      {remaining > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setConfirming(false);
            }}
            inputMode="decimal"
            placeholder={`All ${formatMinor(remaining, charge.currency)}`}
            aria-label="Refund amount"
            className="ui-input-mono !py-2 sm:!w-36"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (kept on the Stripe refund)"
            aria-label="Refund note"
            className="ui-input !py-2"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className={classNames(BTN_SECONDARY, 'shrink-0', confirming && '!border-danger/40 !text-danger')}
          >
            {busy && <Spinner />}
            {confirming ? 'Confirm refund' : 'Refund'}
          </button>
        </div>
      )}

      {error && <ErrorBanner message={error} />}
    </li>
  );
}

function ActionButton({ label, onClick, busy }: { label: string; onClick: () => void; busy: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} className={BTN_SECONDARY}>
      {busy && <Spinner />}
      {label}
    </button>
  );
}
